import { FC } from 'react';
import { translate } from '../../i18n';
import cartIcon from '../../assets/session-expired-cart.svg';
import {
  buildSessionExpiredStoreUrl,
  notifySessionExpiredNavigation,
} from '../../browser/sessionExpiredNavigation';
import { sessionExpiredStyles as styles } from './styles';

export interface SessionExpiredProps {
  onGoHome?: () => void;
  onSignIn?: () => void;
  onContact?: () => void;
  testID?: string;
}

/**
 * SessionExpired — pantalla que se muestra cuando el iframe se abre sin las credenciales del host
 * (sin `window.__CARREFOUR_PAYMENT_INIT__`).
 */
export const SessionExpired: FC<SessionExpiredProps> = ({
  onGoHome,
  onSignIn,
  onContact,
  testID = 'session-expired',
}) => {
  const homeUrl = buildSessionExpiredStoreUrl('home');
  const loginUrl = buildSessionExpiredStoreUrl('login');
  const contactUrl = buildSessionExpiredStoreUrl('contact');
  const nativeHost = typeof window !== 'undefined' && Boolean(window.ReactNativeWebView?.postMessage);

  const onHomeClick = () => {
    onGoHome?.();
    notifySessionExpiredNavigation('home');
  };
  const onSignInClick = () => {
    onSignIn?.();
    notifySessionExpiredNavigation('login');
  };
  const onContactClick = () => {
    onContact?.();
    notifySessionExpiredNavigation('contact');
  };

  const useTopLink = (url: string | undefined) => Boolean(url) && !nativeHost;

  return (
    <main style={styles.container} data-testid={testID}>
      <img src={cartIcon} alt="" width={160} height={140} />
      <h1 style={styles.title}>{translate('paymentsUI.sessionExpired.title')}</h1>
      <p style={styles.message}>{translate('paymentsUI.sessionExpired.message')}</p>
      <div style={styles.actions}>
        {useTopLink(homeUrl) ? (
          <a
            href={homeUrl}
            target="_top"
            rel="noopener noreferrer"
            onClick={onHomeClick}
            style={{ ...styles.primaryButton, textDecoration: 'none' }}
            data-testid={`${testID}-home`}
          >
            {translate('paymentsUI.sessionExpired.goHome')}
            <span aria-hidden="true">→</span>
          </a>
        ) : (
          <button type="button" onClick={onHomeClick} style={styles.primaryButton} data-testid={`${testID}-home`}>
            {translate('paymentsUI.sessionExpired.goHome')}
            <span aria-hidden="true">→</span>
          </button>
        )}
        {useTopLink(loginUrl) ? (
          <a
            href={loginUrl}
            target="_top"
            rel="noopener noreferrer"
            onClick={onSignInClick}
            style={{ ...styles.secondaryButton, textDecoration: 'none' }}
            data-testid={`${testID}-sign-in`}
          >
            {translate('paymentsUI.sessionExpired.signIn')}
          </a>
        ) : (
          <button type="button" onClick={onSignInClick} style={styles.secondaryButton} data-testid={`${testID}-sign-in`}>
            {translate('paymentsUI.sessionExpired.signIn')}
          </button>
        )}
      </div>
      <div style={styles.help}>
        <span style={styles.helpQuestion}>
          <span aria-hidden="true" style={styles.helpIcon}>?</span>
          {translate('paymentsUI.sessionExpired.needHelp')}
        </span>
        {useTopLink(contactUrl) ? (
          <a
            href={contactUrl}
            target="_top"
            rel="noopener noreferrer"
            onClick={onContactClick}
            style={styles.helpLink}
            data-testid={`${testID}-contact`}
          >
            {translate('paymentsUI.sessionExpired.contact')}
          </a>
        ) : (
          <button type="button" onClick={onContactClick} style={styles.helpLink} data-testid={`${testID}-contact`}>
            {translate('paymentsUI.sessionExpired.contact')}
          </button>
        )}
      </div>
    </main>
  );
};
